import { Navigate, useLocation } from 'react-router-dom';
import { useApp } from '@/components/hooks/useApp';
import { usePermissions } from '@/components/hooks/usePermissions';
import { useOnboardingStatus } from './useOnboardingStatus';
import { STEPS, ESSENTIAL_STEP_KEYS } from './onboardingSteps';

// Derivada de STEPS (no una lista duplicada a mano): si se añade un paso
// `navigate` nuevo, el gate lo permite automáticamente sin tocar este archivo.
const NAVIGATE_STEP_PATHS = STEPS.filter((s) => s.mode === 'navigate').map((s) => s.to.split('?')[0]);
const ALWAYS_ALLOWED_PATHS = new Set(['/primeros-pasos', ...NAVIGATE_STEP_PATHS]);

// La tanda esencial es hoy un solo paso: la identidad fiscal. Sin CIF no se
// puede emitir una factura válida, y eso es lo único de lo que no se puede
// prescindir — el certificado, el banco o importar datos aportan mucho pero no
// impiden trabajar. El resto de "Primeros pasos" nunca bloquea.
function isEssentialComplete(status) {
  const done = new Set((status.steps || []).filter((s) => s.completed).map((s) => s.key));
  return ESSENTIAL_STEP_KEYS.every((key) => done.has(key));
}

/**
 * Manda al flujo de bienvenida (/bienvenida) mientras falte la identidad
 * fiscal. Permite los pocos paths a los que el propio checklist navega para
 * completar un paso (STEPS con mode:'navigate'); si no, esos pasos serían
 * imposibles de terminar.
 *
 * LO ESENCIAL MANDA. Hasta 2026-09-17 este gate tenía tres salidas —descartar
 * el checklist, "Lo haré luego" en servidor y el marcador local— y las tres
 * liberaban también el CIF, que es lo único sin lo que una factura no es
 * válida. El resultado eran empresas usando el ERP sin identidad fiscal que se
 * enteraban tarde y en otro sitio: al emitir la primera factura (422
 * `COMPANY_NO_NIF`) o al presentar ante la AEAT.
 *
 * Ahora las tres salidas siguen existiendo —el resto del embudo y del checklist
 * es genuinamente opcional— pero solo se honran cuando lo esencial está hecho.
 * El escape no se mata, se condiciona.
 *
 * Esto es la señalización, no el candado: el candado es `requireCompanyTaxId`
 * en el backend, que devuelve 403 `COMPANY_TAX_ID_REQUIRED` a cualquier ruta
 * salvo las que hacen falta para arreglarlo. Una regla que solo vive aquí la
 * esquiva un `curl` con la cookie de sesión.
 *
 * Solo aplica a admin/owner (`isSystemAdmin || isOwner`, mismo criterio que
 * `requireRole('admin','owner')` en el backend para dismiss/ack, y el mismo que
 * usa el middleware): un rol limitado no puede resolver ni descartar el
 * checklist, así que bloquearlo lo dejaría encerrado sin salida.
 */
export default function OnboardingGate({ children }) {
  const location = useLocation();
  const { company, loading } = useApp();
  const { isSystemAdmin, isOwner, isLoading: permsLoading } = usePermissions();
  const { status, isLoading: statusLoading } = useOnboardingStatus();

  // Fail-open mientras resuelve: evita flash/bloqueo falso en el primer render.
  if (loading || permsLoading || !company?.id || statusLoading || !status) return children;
  if (!isSystemAdmin && !isOwner) return children;
  // Con el nombre y la moneda de una empresa nacida por Google sin dar
  // (`signupDetailsPending`), lo esencial tampoco está: el CIF puede llegar por
  // la tarjeta `fiscal` de Primeros pasos sin pasar por /bienvenida, que es el
  // único sitio que los pide.
  if (isEssentialComplete(status) && !status.signupDetailsPending) return children;
  // Aquí ya NO se consultan los tres escapes —`status.dismissed`,
  // `status.welcomeSkipped` y el marcador local `verix.bienvenida.skipped.*`—
  // porque ninguno libera lo esencial. Siguen vivos y siguen sirviendo para lo
  // suyo: ocultar el panel de "Primeros pasos" y no volver a plantear el embudo.
  if (ALWAYS_ALLOWED_PATHS.has(location.pathname)) return children;

  return <Navigate to="/bienvenida" replace />;
}
